import { selectZenModeEnabled, zenModeChanged } from 'features/layout/features/config/configSlice';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'components/Select';
import { TabPanelInner } from 'components/Tabs';
import { FormControl, MenuItem, SelectChangeEvent, Stack } from '@mui/material';
import InputLabel from 'components/InputLabel';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { logger } from 'utils/logger';
import { setZenModeSetting } from 'utils/zenMode';
import SettingControl from 'components/SettingControl';
import { Switch } from 'components/Switch';
import { selectSupportedLanguages } from 'features/application/applicationSlice';
import { languageChangeRequested } from 'features/application/actions';
import { lobbyJoinSoundChanged, selectLobbyJoinSoundEnabled } from 'features/room/roomSlice';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { AvatarUploadSection } from 'features/users/components/avatar-upload/AvatarUploadSection';

const WorkspaceTab = () => {
  const dispatch = useAppDispatch();

  const { t, i18n } = useTranslation('settings');
  const [language, setLanguage] = useState(i18n.language);

  const supportedLanguages = useAppSelector(selectSupportedLanguages);
  const languages = [...supportedLanguages];
  if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV !== 'production') {
    languages.unshift({ value: 'ach-UG', label: 'In-Context editor' });
  }

  const { hasPermissions } = usePermissions();
  const canControlRoomEntry = hasPermissions(PermissionTypes.controlRoomEntry);
  const canUploadAvatar = hasPermissions(PermissionTypes.avatarUpload);

  const handleLanguageChange = (e: SelectChangeEvent) => {
    const { value } = e.target;
    logger
      .remote({ action: true, tier: 1 })
      .info(`User changed language from ${language} to ${value}`);

    setLanguage(value);

    dispatch(languageChangeRequested(value));
  };

  const zenModeEnabled = useAppSelector(selectZenModeEnabled);
  const lobbySoundEnabled = useAppSelector(selectLobbyJoinSoundEnabled);

  const handleZenModeChange = (checked: boolean) => {
    setZenModeSetting(checked);

    dispatch(zenModeChanged(!checked));
  };

  const handleLobbyJoinSoundChange = () => {
    dispatch(lobbyJoinSoundChanged(!lobbySoundEnabled));
  };

  return (
    <TabPanelInner>
      <Stack spacing={8}>
        {canUploadAvatar && <AvatarUploadSection />}
        <FormControl fullWidth>
          <InputLabel id="language-switcher-label">
            {t('workspace.language_switch_label')}
          </InputLabel>
          <Select
            labelId="language-switcher-label"
            id="language-switcher"
            onChange={handleLanguageChange}
            value={language}
          >
            {languages.map(({ value, label }, i) => (
              <MenuItem key={i} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SettingControl
          label={t('workspace.zen_mode_switch_label')}
          helperText={t('workspace.zen_mode_switch_help')}
          alignItems="flex-start"
          control={
            <Switch
              onChange={(e) => handleZenModeChange(e.target.checked)}
              checked={!zenModeEnabled}
              slotProps={{
                input: {
                  'aria-label': t('workspace.zen_mode_switch_label'),
                },
              }}
            />
          }
        />
        {canControlRoomEntry && (
          <SettingControl
            label={t('workspace.lobby_sound_switch_label')}
            helperText={t('workspace.lobby_sound_switch_help')}
            alignItems="flex-start"
            control={
              <Switch
                onChange={handleLobbyJoinSoundChange}
                checked={lobbySoundEnabled}
                slotProps={{
                  input: {
                    'aria-label': lobbySoundEnabled
                      ? t('workspace.lobby_sound_disable_aria_label')
                      : t('workspace.lobby_sound_enable_aria_label'),
                  },
                }}
              />
            }
          />
        )}
      </Stack>
    </TabPanelInner>
  );
};

export default WorkspaceTab;
